/**
 * Reports library — presentations only, clustered by the template they were
 * made from (Proposals, Wrap-ups, Creator performance, …). Cards show the
 * real cover in the brand theme, a spinner while Lykke is still building the
 * deck, and delete / present on hover.
 */
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Trash2, Play, AlertTriangle, Search, RotateCcw, Sparkles, Lock, Users } from 'lucide-react';
import { toast } from 'sonner';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { PresentationModal } from '@/cowork/omniad/components/PresentationModal';
import { SlideRenderer, deriveTheme } from '@/cowork/omniad/components/slides/SlideRenderer';
import { REPORT_TEMPLATES } from './templates';

type Row = Awaited<ReturnType<typeof coworkApi.presentations.list>>['presentations'][number];

/** Clusters shown as chips, in order. Template ids map onto them. */
const CLUSTERS: Array<{ id: string; label: string; templateIds: string[] }> = [
  { id: 'wrapup', label: 'Wrap-ups', templateIds: ['campaign-wrapup', 'campaign-results'] },
  { id: 'proposal', label: 'Proposals', templateIds: ['proposal', 'complete-proposal'] },
  { id: 'creators', label: 'Creator performance', templateIds: ['creator-performance'] },
  { id: 'monthly', label: 'Monthly', templateIds: ['monthly-report'] },
  { id: 'showcase', label: 'Showcases', templateIds: ['content-showcase'] },
  { id: 'case', label: 'Case studies', templateIds: ['case-study'] },
  { id: 'other', label: 'Other', templateIds: ['blank'] },
];

/** Decks built in the chat carry the kind Lykke gave them; template decks are filed by their template. */
const KIND_CLUSTER: Record<string, string> = { results: 'wrapup', proposal: 'proposal', creators: 'creators', monthly: 'monthly', showcase: 'showcase', case: 'case' };
/** The card badge says what the deck is. */
const KIND_NAME: Record<string, string> = { results: 'Wrap-up', proposal: 'Proposal', creators: 'Creators', monthly: 'Monthly', showcase: 'Showcase', case: 'Case study' };
function clusterOf(row: Row): string {
  const kind = (row as Row & { kind?: string | null }).kind;
  if (kind && KIND_CLUSTER[kind]) return KIND_CLUSTER[kind];
  const t = row.templateId || '';
  const hit = CLUSTERS.find((c) => c.templateIds.includes(t));
  return hit ? hit.id : 'other';
}

function timeAgo(iso: string): string {
  const d = Date.now() - new Date(iso).getTime();
  const m = Math.floor(d / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function ReportsLibrary({ onOpen, action, lead }: { onOpen: (id: string) => void; action?: ReactNode; /** Rendered between the header and the filters (the need composer). */ lead?: ReactNode }) {
  const qc = useQueryClient();
  const [cluster, setCluster] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [presentId, setPresentId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  // Re-render every 15 s so "started 2m ago" keeps moving while a build runs.
  const [, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((n) => n + 1), 15000); return () => clearInterval(t); }, []);

  const query = useQuery({
    queryKey: ['cowork', 'reports', 'library'],
    queryFn: () => coworkApi.presentations.list(),
    // Generation status: poll every 10s while running; pause when the tab is hidden.
    refetchInterval: (q) => (q.state.data?.presentations.some((p) => p.generation?.status === 'running') ? 10_000 : false),
    refetchIntervalInBackground: false,
    // Decks are created elsewhere (modal, builder, agents): never trust a cached list when the page is shown again.
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    staleTime: 0,
  });
  const rows = useMemo(() => query.data?.presentations ?? [], [query.data]);

  // Lykke-built decks flip from running → done: nudge the user.
  const [seenRunning, setSeenRunning] = useState<Set<string>>(new Set());
  useEffect(() => {
    const running = new Set(rows.filter((r) => r.generation?.status === 'running').map((r) => r.id));
    for (const id of seenRunning) {
      if (!running.has(id)) {
        const row = rows.find((r) => r.id === id);
        if (row?.generation?.status === 'done') toast.success(`"${row.title}" is ready`);
        if (row?.generation?.status === 'failed') toast.error(`"${row.title}" could not be built`);
      }
    }
    if (running.size !== seenRunning.size || [...running].some((id) => !seenRunning.has(id))) setSeenRunning(running);
  }, [rows, seenRunning]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: rows.length };
    for (const r of rows) c[clusterOf(r)] = (c[clusterOf(r)] || 0) + 1;
    return c;
  }, [rows]);

  const visible = rows.filter((r) => (cluster === 'all' || clusterOf(r) === cluster) && (!search.trim() || r.title.toLowerCase().includes(search.trim().toLowerCase())));

  const retry = async (row: Row) => {
    try {
      await coworkApi.presentations.regenerate(row.id);
      toast.message('Lykke is rebuilding the report');
      await qc.invalidateQueries({ queryKey: ['cowork', 'reports', 'library'] });
    } catch (e) {
      toast.error(`Could not retry: ${(e as Error).message}`);
    }
  };

  const remove = async (row: Row) => {
    if (!confirm(`Delete "${row.title}"? This cannot be undone.`)) return;
    setDeleting(row.id);
    try {
      await coworkApi.presentations.delete(row.id);
      await qc.invalidateQueries({ queryKey: ['cowork', 'reports', 'library'] });
      await qc.invalidateQueries({ queryKey: ['cowork', 'deliverables'] });
    } catch (e) {
      toast.error(`Delete failed: ${(e as Error).message}`);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="p-6">
      <style>{'@keyframes reportsIndeterminate { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }'}</style>
      {presentId && <PresentationModal presentationId={presentId} onClose={() => setPresentId(null)} onEdit={(id) => { setPresentId(null); onOpen(id); }} />}
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[24px] text-[#101828]" style={{ fontWeight: 700 }}>Deck studio</h1>
        </div>
        {action}
      </div>
      {lead}

      <div className="mb-5 flex flex-wrap items-center gap-2">
        {[{ id: 'all', label: 'All' }, ...CLUSTERS].map((c) => {
          const n = counts[c.id] || 0;
          if (c.id !== 'all' && n === 0) return null;
          const active = cluster === c.id;
          return (
            <button key={c.id} onClick={() => setCluster(c.id)} className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium transition ${active ? 'border-[#be43ff] bg-[#fcf7ff] text-[#7c3aed]' : 'border-[#eaecf0] text-[#344054] hover:border-[#d0d5dd]'}`}>
              {c.label} <span className={`text-[11px] ${active ? 'text-[#a855f7]' : 'text-[#98a2b3]'}`}>{n}</span>
            </button>
          );
        })}
        <div className="relative ml-auto w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#98a2b3]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search reports…" className="w-full rounded-lg border border-[#d0d5dd] py-1.5 pl-8 pr-2 text-[13px]" />
        </div>
      </div>

      {query.isLoading && <div className="flex justify-center py-20"><Loader2 className="h-5 w-5 animate-spin text-[#667085]" /></div>}
      {!query.isLoading && visible.length === 0 && (
        <div className="py-20 text-center text-[13px] text-[#98a2b3]">{rows.length ? 'Nothing in this group yet.' : 'No reports yet — create one, or ask an agent for a deck.'}</div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {visible.map((row) => {
          const theme = deriveTheme(row.brand || {});
          const running = row.generation?.status === 'running';
          const failed = row.generation?.status === 'failed';
          const kindName = KIND_NAME[(row as Row & { kind?: string | null }).kind || ''];
          const tplName = kindName || row.templateName || REPORT_TEMPLATES.find((t) => t.id === row.templateId)?.name || (row.sourceType === 'agent_skill' ? 'Agent deck' : 'Deck');
          return (
            <div key={row.id} className="group relative overflow-hidden rounded-xl border border-[#eaecf0] bg-white transition hover:border-[#d0d5dd] hover:shadow-md">
              <button type="button" onClick={() => onOpen(row.id)} className="block w-full text-left">
                <div style={{ position: 'relative', aspectRatio: '16 / 9', containerType: 'size', background: theme.bg, pointerEvents: 'none', overflow: 'hidden' } as CSSProperties}>
                  {row.cover ? <SlideRenderer slide={row.cover} index={0} theme={theme} title={row.title} /> : null}
                  {running && (() => {
                    const g = row.generation!;
                    const isProposal = g.kind === 'proposal';
                    const pct = isProposal && g.stepsTotal ? Math.round(((g.step || 0) / g.stepsTotal) * 100) : null;
                    return (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/85 px-6 backdrop-blur-[1px]">
                        {isProposal ? <Sparkles className="h-6 w-6 text-[#be43ff]" /> : <Loader2 className="h-6 w-6 animate-spin text-[#be43ff]" />}
                        <div className="text-[12px] font-semibold text-[#101828]">{isProposal ? `Proposal in progress · ${g.stepLabel || 'Setup'} (${g.step || 0} of ${g.stepsTotal || 6})` : 'Lykke is building…'}</div>
                        <div className="h-1.5 w-full max-w-[200px] overflow-hidden rounded-full bg-[#eaecf0]">
                          {pct !== null ? <div className="h-full rounded-full bg-[#be43ff] transition-all" style={{ width: `${Math.max(6, pct)}%` }} /> : <div className="h-full w-1/3 animate-[shimmer_1.4s_ease-in-out_infinite] rounded-full bg-[#be43ff]" style={{ animation: 'reportsIndeterminate 1.4s ease-in-out infinite' }} />}
                        </div>
                        <div className="text-[10px] text-[#98a2b3]">{isProposal ? 'Click to resume' : g.startedAt ? `started ${timeAgo(g.startedAt)}` : ''}</div>
                      </div>
                    );
                  })()}
                </div>
                <div className="px-4 pb-3 pt-3">
                  <div className="line-clamp-2 text-[14px] font-semibold text-[#101828]">{row.title}</div>
                  <div className="mt-1.5 flex items-center gap-2 text-[11px] text-[#667085]">
                    <span className="rounded-full bg-[#f4ecff] px-1.5 py-0.5 text-[#7c3aed]">{tplName}</span>
                    {row.visibility === 'private' && <span title="Only you can see this" className="flex items-center gap-1 text-[#667085]"><Lock className="h-3 w-3" /> only me</span>}
                    {row.visibility === 'members' && <span title="Shared with picked team members" className="flex items-center gap-1 text-[#667085]"><Users className="h-3 w-3" /> {row.allowedUserIds?.length || 0}</span>}
                    {typeof row.slideCount === 'number' && <span>{row.slideCount} page{row.slideCount === 1 ? '' : 's'}</span>}
                    {running && row.generation?.kind === 'proposal' && <span className="text-[#7c3aed]">in progress</span>}
                    {failed && <span className="flex items-center gap-1 text-[#b42318]" title={row.generation?.error || ''}><AlertTriangle className="h-3 w-3" /> build failed</span>}
                    {failed && <span role="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); retry(row); }} className="flex items-center gap-1 font-semibold text-[#7c3aed] hover:underline"><RotateCcw className="h-3 w-3" /> Retry</span>}
                    <span className="ml-auto">{timeAgo(row.updatedAt)}</span>
                  </div>
                </div>
              </button>
              <div className="absolute right-2 top-2 z-10 flex gap-1 opacity-0 transition group-hover:opacity-100">
                {!running && (
                  <button onClick={(e) => { e.stopPropagation(); setPresentId(row.id); }} title="Present" className="rounded-md bg-white/95 p-1.5 text-[#344054] shadow hover:text-[#7c3aed]"><Play className="h-3.5 w-3.5" /></button>
                )}
                <button onClick={(e) => { e.stopPropagation(); remove(row); }} title="Delete" className="rounded-md bg-white/95 p-1.5 text-[#344054] shadow hover:text-[#b42318]">
                  {deleting === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
